// Hover tooltip with the retro TUI look, positioned so its content fits on screen.
//
// Mark a trigger with one of:
//   data-tui-tooltip="plain text"                       text content
//   data-tui-tooltip-html="&lt;b&gt;html&lt;/b&gt;"     HTML string (Razor's attribute encoding is all it needs)
//   data-tui-tooltip + a child <template class="tui-tooltip-content">…</template>
//                                                       rich markup authored in the view
//   data-tui-tooltip + data-tui-tooltip-template="id"   the same, but the <template> lives elsewhere on the
//                                                       page, so several triggers can share one
// Optional: data-tui-tooltip-placement="top|bottom|left|right" (preferred side, default top),
// data-tui-tooltip-class="…" (extra class on the popup for per-use styling) and data-tui-tooltip-click
// (a button whose job is to show the tooltip: hover and focus do nothing, a click toggles it, and it
// stays open until a click elsewhere, a second click on the button, or Escape).
//
// One popup element is shared by every trigger and events are delegated from the document, so
// triggers added later (auto-refresh, innerHTML swaps, preact renders) need no wiring. Loaded on
// every page via global.ts; window.tuiTooltip exposes show/hide for page bundles.

var TRIGGER_SELECTOR = "[data-tui-tooltip], [data-tui-tooltip-html]";
var OPEN_DELAY_MS = 250;
var CLOSE_DELAY_MS = 100;
var VIEWPORT_MARGIN = 8;
var TRIGGER_GAP = 10;
var MAX_WIDTH = 640;
var ARROW_INSET = 14;

type Placement = "top" | "bottom" | "left" | "right";

var popup: HTMLDivElement;
var popupBody: HTMLDivElement;
var popupArrow: HTMLDivElement;
var activeTrigger: HTMLElement | null = null;
var pendingTrigger: HTMLElement | null = null;
var openTimer: number | null = null;
var closeTimer: number | null = null;
var extraClass = "";

function createPopup() {
    popup = document.createElement("div");
    popup.className = "tui-tooltip";
    popup.id = "tui-tooltip";
    popup.setAttribute("role", "tooltip");
    popup.innerHTML = '<div class="tui-tooltip-body"></div><div class="tui-tooltip-arrow"></div>';
    document.body.appendChild(popup);

    popupBody = popup.querySelector(".tui-tooltip-body")!;
    popupArrow = popup.querySelector(".tui-tooltip-arrow")!;

    popup.addEventListener("mouseenter", cancelClose);
    popup.addEventListener("mouseleave", function () {
        if (activeTrigger && !opensOnClick(activeTrigger)) {
            scheduleClose();
        }
    });
}

function opensOnClick(trigger: HTMLElement): boolean {
    return trigger.hasAttribute("data-tui-tooltip-click");
}

function findTrigger(target: EventTarget | null): HTMLElement | null {
    if (!(target instanceof Element)) {
        return null;
    }
    return target.closest(TRIGGER_SELECTOR) as HTMLElement | null;
}

function findTemplate(trigger: HTMLElement): HTMLTemplateElement | null {
    var templateId = trigger.getAttribute("data-tui-tooltip-template");
    if (templateId) {
        return document.getElementById(templateId) as HTMLTemplateElement | null;
    }
    return trigger.querySelector<HTMLTemplateElement>("template.tui-tooltip-content");
}

function fillContent(trigger: HTMLElement) {
    popupBody.innerHTML = "";

    var template = findTemplate(trigger);
    if (template) {
        popupBody.appendChild(template.content.cloneNode(true));
        return;
    }

    var html = trigger.getAttribute("data-tui-tooltip-html");
    if (html) {
        popupBody.innerHTML = html;
        return;
    }

    popupBody.textContent = trigger.getAttribute("data-tui-tooltip") || "";
}

function hasContent(trigger: HTMLElement): boolean {
    return !!findTemplate(trigger)
        || !!trigger.getAttribute("data-tui-tooltip-html")
        || !!trigger.getAttribute("data-tui-tooltip");
}

function preferredPlacement(trigger: HTMLElement): Placement {
    var value = trigger.getAttribute("data-tui-tooltip-placement");
    if (value == "bottom" || value == "left" || value == "right") {
        return value;
    }
    return "top";
}

function position(trigger: HTMLElement) {
    var viewportWidth = document.documentElement.clientWidth;
    var viewportHeight = document.documentElement.clientHeight;
    var rect = trigger.getBoundingClientRect();

    // Natural size first, capped only by the viewport width; the chosen side may then cap the height.
    popup.style.maxWidth = Math.min(MAX_WIDTH, viewportWidth - VIEWPORT_MARGIN * 2) + "px";
    popup.style.maxHeight = "";
    var width = popup.offsetWidth;
    var height = popup.offsetHeight;

    var space: Record<Placement, number> = {
        top: rect.top - TRIGGER_GAP - VIEWPORT_MARGIN,
        bottom: viewportHeight - rect.bottom - TRIGGER_GAP - VIEWPORT_MARGIN,
        left: rect.left - TRIGGER_GAP - VIEWPORT_MARGIN,
        right: viewportWidth - rect.right - TRIGGER_GAP - VIEWPORT_MARGIN
    };

    var order: Placement[] = ["top", "bottom", "right", "left"];
    var preferred = preferredPlacement(trigger);
    order.splice(order.indexOf(preferred), 1);
    order.unshift(preferred);

    var placement: Placement | null = null;
    for (var i = 0; i < order.length; i++) {
        var candidate = order[i];
        if (candidate == "top" || candidate == "bottom") {
            if (height <= space[candidate]) {
                placement = candidate;
                break;
            }
        }
        else if (width <= space[candidate] && height <= viewportHeight - VIEWPORT_MARGIN * 2) {
            placement = candidate;
            break;
        }
    }

    // Nothing fits whole: take the taller of above/below and let the body scroll.
    if (!placement) {
        placement = space.top >= space.bottom ? "top" : "bottom";
        popup.style.maxHeight = Math.max(80, space[placement]) + "px";
        width = popup.offsetWidth;
        height = popup.offsetHeight;
    }

    var x: number;
    var y: number;
    if (placement == "top" || placement == "bottom") {
        x = rect.left + rect.width / 2 - width / 2;
        y = placement == "top" ? rect.top - TRIGGER_GAP - height : rect.bottom + TRIGGER_GAP;
    }
    else {
        x = placement == "left" ? rect.left - TRIGGER_GAP - width : rect.right + TRIGGER_GAP;
        y = rect.top + rect.height / 2 - height / 2;
    }

    x = Math.max(VIEWPORT_MARGIN, Math.min(x, viewportWidth - VIEWPORT_MARGIN - width));
    y = Math.max(VIEWPORT_MARGIN, Math.min(y, viewportHeight - VIEWPORT_MARGIN - height));

    popup.style.left = Math.round(x) + "px";
    popup.style.top = Math.round(y) + "px";
    popup.setAttribute("data-placement", placement);

    // The arrow tracks the trigger even when the box had to slide sideways to stay on screen.
    if (placement == "top" || placement == "bottom") {
        var arrowX = rect.left + rect.width / 2 - x;
        arrowX = Math.max(ARROW_INSET, Math.min(arrowX, width - ARROW_INSET));
        popupArrow.style.left = Math.round(arrowX) + "px";
        popupArrow.style.top = "";
    }
    else {
        var arrowY = rect.top + rect.height / 2 - y;
        arrowY = Math.max(ARROW_INSET, Math.min(arrowY, height - ARROW_INSET));
        popupArrow.style.top = Math.round(arrowY) + "px";
        popupArrow.style.left = "";
    }
}

function show(trigger: HTMLElement) {
    cancelOpen();
    cancelClose();

    if (activeTrigger && activeTrigger !== trigger) {
        detach();
    }

    if (extraClass) {
        popup.classList.remove(extraClass);
    }
    extraClass = trigger.getAttribute("data-tui-tooltip-class") || "";
    if (extraClass) {
        popup.classList.add(extraClass);
    }

    fillContent(trigger);
    activeTrigger = trigger;
    trigger.setAttribute("aria-describedby", popup.id);
    // Lets a trigger keep its hover look while the mouse is over the popup (see .badge-validation).
    trigger.classList.add("tui-tooltip-active");

    // The closed state is visibility: hidden rather than display: none, so it can be measured before it opens.
    position(trigger);
    popup.classList.add("open");
}

function detach() {
    if (activeTrigger) {
        activeTrigger.removeAttribute("aria-describedby");
        activeTrigger.classList.remove("tui-tooltip-active");
    }
    activeTrigger = null;
}

// Closes the open tooltip only. A pending open for another trigger is left alone: moving quickly from
// one badge to the next has the first one's close timer fire while the next one is still counting down.
function hide() {
    cancelClose();
    if (!activeTrigger) {
        return;
    }
    detach();
    popup.classList.remove("open");
    popupBody.scrollTop = 0;
}

function scheduleOpen(trigger: HTMLElement) {
    if (trigger === activeTrigger) {
        cancelClose();
        return;
    }
    if (trigger === pendingTrigger) {
        return;
    }
    // Sliding from one trigger onto the next (along a row of squares or pips) swaps the open tooltip right away; the
    // delay only guards the first one.
    if (activeTrigger && !opensOnClick(activeTrigger)) {
        show(trigger);
        return;
    }
    cancelOpen();
    pendingTrigger = trigger;
    openTimer = window.setTimeout(function () {
        openTimer = null;
        pendingTrigger = null;
        if (trigger.isConnected) {
            show(trigger);
        }
    }, OPEN_DELAY_MS);
}

function cancelOpen() {
    if (openTimer !== null) {
        window.clearTimeout(openTimer);
        openTimer = null;
    }
    pendingTrigger = null;
}

function scheduleClose() {
    if (closeTimer !== null) {
        return;
    }
    closeTimer = window.setTimeout(function () {
        closeTimer = null;
        hide();
    }, CLOSE_DELAY_MS);
}

function cancelClose() {
    if (closeTimer !== null) {
        window.clearTimeout(closeTimer);
        closeTimer = null;
    }
}

function touchOnly(): boolean {
    return !!window.matchMedia && window.matchMedia("(hover: none)").matches;
}

function init() {
    createPopup();

    document.addEventListener("mouseover", function (e) {
        var trigger = findTrigger(e.target);
        if (trigger && hasContent(trigger) && !opensOnClick(trigger)) {
            scheduleOpen(trigger);
        }
    });

    document.addEventListener("mouseout", function (e) {
        var trigger = findTrigger(e.target);
        if (!trigger) {
            return;
        }
        // Moving between children of the same trigger is not a leave.
        var to = e.relatedTarget;
        if (to instanceof Node && trigger.contains(to)) {
            return;
        }
        if (trigger === pendingTrigger) {
            cancelOpen();
        }
        // A click-opened tooltip stays put: the mouse wandering off, or focus moving into the popup
        // (clicking a link in it), must not close it.
        if (trigger === activeTrigger && !opensOnClick(trigger)) {
            scheduleClose();
        }
    });

    document.addEventListener("focusin", function (e) {
        var trigger = findTrigger(e.target);
        if (trigger && hasContent(trigger) && !opensOnClick(trigger)) {
            show(trigger);
        }
    });

    document.addEventListener("focusout", function (e) {
        var trigger = findTrigger(e.target);
        if (trigger && trigger === activeTrigger && !opensOnClick(trigger)) {
            scheduleClose();
        }
    });

    // No hover on touch screens, so a tap toggles instead. Click-to-open triggers toggle everywhere.
    document.addEventListener("click", function (e) {
        var trigger = findTrigger(e.target);
        if (trigger && hasContent(trigger) && (touchOnly() || opensOnClick(trigger))) {
            if (trigger === activeTrigger) {
                hide();
            }
            else {
                show(trigger);
            }
            return;
        }
        if (activeTrigger && !popup.contains(e.target as Node) && !(trigger && trigger === activeTrigger)) {
            hide();
        }
    });

    document.addEventListener("keydown", function (e) {
        if (e.key === "Escape") {
            cancelOpen();
            hide();
        }
    });

    window.addEventListener("scroll", function () {
        if (activeTrigger) {
            hide();
        }
    });

    window.addEventListener("resize", function () {
        if (activeTrigger) {
            position(activeTrigger);
        }
    });
}

(window as any).tuiTooltip = {
    show: function (trigger: HTMLElement) {
        show(trigger);
    },
    hide: function () {
        cancelOpen();
        hide();
    }
};

document.addEventListener("DOMContentLoaded", init);

// tui-modal.ts is a plain script, so without this the two files would share one global scope and clash on names.
export {};
